<template>
  <div class="post-detail" v-if="post">
    <div class="post-media">
      <template v-if="post.fileType && isVideo(post.fileType)">
        <video
          ref="videoElement"
          controls
          class="post-video"
        >
          <source :src="post.imageUrl || post.fileUrl" type="video/mp4" />
          Twoja przeglądarka nie obsługuje odtwarzania wideo.
        </video>
        <div class="px-6">
          <p class="post-text">{{ post.content }}</p>
          <p class="post-author">{{ post.author || "Anonimowo" }}</p>
          <p class="post-date">{{ displayDate(post.createdAt) }}</p>
        </div>
      </template>
      <template v-else>
        <div class="image-container">
          <v-img
            v-if="post.imageUrl || post.fileUrl"
            :src="post.imageUrl || post.fileUrl"
            class="post-image"
          ></v-img>
          <div class="overlay">
            <p class="post-text">{{ post.content }}</p>
            <p class="post-author">{{ post.author || "Anonimowo" }}</p>
            <p class="post-date">{{ displayDate(post.createdAt) }}</p>
          </div>
        </div>
      </template>
    </div>
  </div>
  <div v-else class="loading-placeholder">Ładowanie wpisu...</div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import { db } from "@/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { useRoute } from "vue-router";
import { format, formatDistanceToNow, isBefore, subDays } from "date-fns";

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const post = ref(null);
    const route = useRoute();
    const postId = ref(props.id || route.params.id);
    const videoElement = ref(null);
    let observer = null;

    onMounted(async () => {
      await fetchPost();

      // Ustawienie IntersectionObserver dla wideo
      if (videoElement.value) {
        observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              videoElement.value.play();
            } else {
              videoElement.value.pause();
            }
          },
          { threshold: 0.5 } // Wideo musi być w 50% widoczne
        );
        observer.observe(videoElement.value);
      }
    });

    onUnmounted(() => {
      // Odłączenie IntersectionObserver
      if (observer && videoElement.value) {
        observer.unobserve(videoElement.value);
      }
    });

    const fetchPost = async () => {
      if (postId.value) {
        const postRef = doc(db, "posts", postId.value);
        const snapshot = await getDoc(postRef);
        if (snapshot.exists()) {
          post.value = { id: snapshot.id, ...snapshot.data() };
        } else {
          console.log("No such document!");
        }
      }
    };

    const displayDate = (timestamp) => {
      if (timestamp && timestamp.toDate) {
        const date = timestamp.toDate();
        const oneDayAgo = subDays(new Date(), 1);

        if (isBefore(date, oneDayAgo)) {
          return format(date, "yyyy-MM-dd HH:mm:ss"); // Wyświetl pełną datę z godziną
        } else {
          return `${formatDistanceToNow(date, { addSuffix: true })}`; // Wyświetl "jak dawno temu"
        }
      }
      return "";
    };

    const isVideo = (fileType) => {
      return fileType && fileType.startsWith("video/");
    };

    return {
      post,
      displayDate,
      isVideo,
      videoElement,
    };
  },
};
</script>

<style scoped>
.post-detail {
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  aspect-ratio: 3 / 4;
}

.image-container {
  position: relative;
}

.post-image {
  width: 100%;
  aspect-ratio: 3 / 4;
}

.overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.7)); /* Gradientowe tło */
  color: white;
  padding: 16px;
  text-align: left; /* Wyrównanie tekstu do lewej */
}

.post-text {
  font-size: 1.2em;
  font-weight: bold;
  margin-bottom: 4px;
}

.post-author {
  font-size: 1em;
  margin-bottom: 4px;
}

.post-date {
  font-size: 0.9em;
  color: lightgray;
}

.post-video {
  width: 100%;
  aspect-ratio: 3 / 4;
}

.loading-placeholder {
  font-size: 1.5em;
  color: gray;
  text-align: center;
}
</style>
